// src/platform/components/LandingPage.jsx
import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { ContactService } from '../../shared/services/ContactService';

const LandingPage = ({ onSignIn, initialContactOpen = false }) => {
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(initialContactOpen);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    setContactSuccess(false);
    
    try {
      console.log('📝 Submitting contact form...');
      
      // Validate form data
      if (!contactForm.name.trim()) {
        throw new Error('Name is required');
      }
      if (!contactForm.email.trim()) {
        throw new Error('Email is required');
      }
      if (!contactForm.message.trim()) {
        throw new Error('Message is required');
      }
      
      console.log('✅ Form validation passed');
      
      // Submit to database
      const submissionData = {
        name: contactForm.name.trim(),
        email: contactForm.email.trim(),
        message: contactForm.message.trim()
      };
      
      console.log('🚀 Calling ContactService.submitContactForm...');
      const submissionId = await ContactService.submitContactForm(submissionData);
      console.log('✅ Contact form submitted successfully:', submissionId);
      
      // Show success message
      setContactSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setContactForm({ name: '', email: '', message: '' });
        setShowContactForm(false);
        setContactSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Contact form error:', error);
      console.error('❌ Error details:', error.message, error.code);
      
      // Show specific error message
      if (error.message.includes('Name is required') || 
          error.message.includes('Email is required') || 
          error.message.includes('Message is required')) {
        setContactError(error.message);
      } else if (error.code === 'permission-denied') {
        setContactError('Permission denied. Please check Firestore security rules.');
      } else if (error.code === 'unavailable') {
        setContactError('Service temporarily unavailable. Please try again.');
      } else {
        setContactError(`Error: ${error.message || 'Unable to submit message. Please try again later.'}`);
      }
    } finally {
      setContactLoading(false);
    }
  };

  const handleSignIn = async () => {
    setLoading(true);
    try {
      // Call the parent's sign in handler
      await onSignIn();
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900">
      {/* Header */}
      <header className="px-6 py-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/tenants/inmocean/config/ChainTo.png" 
              alt="Supply ChainTo Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="text-xl font-semibold text-white">
              ChainTo
            </span>
          </div>
          
          <button
            onClick={handleSignIn}
            className="text-sm text-gray-300 hover:text-white transition-colors"
          >
            Sign In
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="/tenants/inmocean/config/ChainTo.png" 
              alt="Supply ChainTo Logo" 
              className="w-24 h-24 object-contain"
            />
          </div>

          {/* Hero */}
          <h1 className="text-5xl font-bold mb-2 leading-tight">
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Supply Chain</span>
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">To</span>
          </h1>

          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Hi</span>
            <span className="text-white mx-2">+</span>
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Ai</span>
          </h2>

          <p className="text-2xl text-gray-400 mb-8 font-medium tracking-wide">
            Experience meets Tech
          </p>

          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Intelligent automation and data management for modern supply chains
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-gray-700 mt-32">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-sm text-gray-400">
          <div>© 2025 Supply ChainTo. All rights reserved.</div>
          <div className="flex space-x-6">
            <a href="#" className="hover:text-gray-300 transition-colors">Privacy</a>
            <a href="#" className="hover:text-gray-300 transition-colors">Terms</a>
            <button
              onClick={() => setShowContactForm(true)}
              className="hover:text-gray-300 transition-colors"
            >
              Contact
            </button>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Contact Us</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              {contactError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{contactError}</p>
                </div>
              )}
              
              {contactSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">Message submitted successfully! We'll get back to you soon.</p>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={contactLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {contactLoading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
